<template>
  <div>
    <v-dialog v-model="newSale.dialog" persistent max-width="800">
      <v-card class="px-4">
        <v-card-title>
          <span class="headline">New Sale</span>
        </v-card-title>
        <v-form ref="form">
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-select
                  label="Select Item *"
                  :items="availItems"
                  v-model="newSale.item"
                  :rules="[rules.required]"
                  :loading="loadingItems"
                  item-text="displayName"
                  item-value="dedicationItemID"
                  return-object
                  placeholder="Start typing..."
                >
                  <template v-slot:item="{ item }">
                    <div class="item-row">
                      <span class="item-id">{{ item.dedicationItemID }}</span>
                      <span class="item-details">
                        Site: <strong>{{ item.buildingCode }}</strong> Name:
                        <strong>{{ item.name }}</strong>
                      </span>
                      <span class="item-meta">
                        {{ item.type }} - {{ formatPrice(item.suggestedPrice) }}
                      </span>
                    </div>
                  </template>
                </v-select>
              </v-flex>

              <v-flex xs3 md4>
                <v-text-field
                  label="Offer Price"
                  v-model.number="newSale.offerPrice"
                  type="number"
                  prefix="$"
                  :rules="[rules.positiveNumber]"
                ></v-text-field>
              </v-flex>

              <v-flex xs3 md3 class="ml-5">
                <v-checkbox
                  v-model="newSale.status"
                  label="Sale Is Completed"
                  color="primary"
                  hide-details
                ></v-checkbox>
              </v-flex>

              <v-flex xs3 md3>
                <v-checkbox
                  v-model="newSale.isLegacy"
                  label="Is Legacy"
                  color="primary"
                  hide-details
                ></v-checkbox>
              </v-flex>

              <v-flex xs12>
                <v-textarea
                  label="Description"
                  v-model="newSale.description"
                  rows="3"
                  counter
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>

          <v-card-actions class="pb-3">
            <v-spacer></v-spacer>
            <v-btn dark @click="closeNewSaleDialog" color="default"
              >Cancel</v-btn
            >
            <v-btn
              :loading="loading"
              :disabled="loading"
              dark
              @click="handleSave"
              color="success"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatCurrency } from "../../utils/helpers.js";

export default {
  name: "NewSaleDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loading: false,
      loadingItems: false,
      items: [],
      newSale: {
        dialog: false,
        item: null,
        offerPrice: null,
        description: "",
        isLegacy: false,
        status: "",
      },
      rules: {
        required: (value) => !!value || "Required.",
        positiveNumber: (v) => v >= 0 || "Price must be positive",
      },
    };
  },

  computed: {
    availItems() {
      return this.items.filter((item) => item.status === "Available");
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.newSale.dialog = val;
      },
    },
    "newSale.dialog"(val) {
      this.$emit("input", val);
      if (!val) {
        this.resetForm();
      }
    },
  },

  created() {
    this.loadItems();
  },

  methods: {
    async loadItems() {
      this.loadingItems = true;
      try {
        const response = await this.$http.get(
          "odata/dedicationItems/?$orderby=buildingCode"
        );
        const data = response.data || response;
        const items = data.value || [];

        this.items = items.map((item) => {
          item.displayName = "Site: " + item.buildingCode;

          return item;
        });
      } catch (error) {
        console.error("Error loading items:", error);
        this.$emit("error", error);
      } finally {
        this.loadingItems = false;
      }
    },

    async handleSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (
        !this.initialData ||
        !this.initialData.donor ||
        !this.initialData.donor.personID
      ) {
        console.error("Missing donor information");
        return;
      }

      const payload = {
        personID: this.initialData.donor.personID,
        dedicationItemID: this.newSale.item.dedicationItemID,
        SaleDescription: this.newSale.description,
        offerPrice: this.newSale.offerPrice || 0,
        finalPrice: this.newSale.offerPrice || 0,
        isLegacy: this.newSale.isLegacy,
        status: this.newSale.status ? "Completed" : "",
      };

      this.loading = true;
      try {
        await this.$http.post("api/dedications/AddDedicationSale", payload);
        this.$emit("saved");
        this.closeNewSaleDialog();
        this.$notify({
          group: "notification",
          type: "success",
          text: "Sale added successfully!",
        });
      } catch (error) {
        console.error("Error saving sale:", error);
        this.$emit("save-error", error);
      } finally {
        this.loading = false;
      }
    },

    closeNewSaleDialog() {
      this.newSale.dialog = false;
    },

    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
        this.$refs.form.resetValidation();
      }
      this.newSale = {
        dialog: false,
        item: null,
        offerPrice: null,
        description: "",
        isLegacy: false,
      };
    },

    formatPrice(price) {
      return formatCurrency(price);
    },
  },
};
</script>

<style scoped>
.item-row {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 0;
}

.item-id {
  margin-left: 8px;
  color: #9e9e9e;
  font-size: 12px;
  margin-right: 12px;
}

.item-details {
  flex: 1;
}

.item-meta {
  margin-left: 8px;
  color: #9e9e9e;
  font-size: 12px;
}
</style>
